'use client'

import { cn } from '@/lib/utils'
import { useLingui } from '@lingui/react'
import { motion } from 'framer-motion'
import React from 'react'

const ElegantShape = React.memo(
  ({
    className,
    delay = 0,
    width = 400,
    height = 100,
    rotate = 0,
    gradient = 'from-white/[0.08]'
  }: {
    className?: string
    delay?: number
    width?: number
    height?: number
    rotate?: number
    gradient?: string
  }) => {
    return (
      <motion.div
        initial={{
          opacity: 0,
          rotate: rotate - 15
        }}
        animate={{
          opacity: 1,
          rotate: rotate
        }}
        transition={{
          duration: 2.4,
          delay,
          ease: [0.23, 0.86, 0.39, 0.96],
          opacity: { duration: 1.2 }
        }}
        className={cn('absolute', className)}
      >
        <motion.div
          animate={{
            opacity: [1, 0.7, 1]
          }}
          transition={{
            duration: 15,
            repeat: Number.POSITIVE_INFINITY,
            ease: 'easeInOut'
          }}
          style={{
            width,
            height
          }}
          className='relative'
        >
          <div
            className={cn(
              'absolute inset-0 rounded-full',
              'bg-gradient-to-r to-transparent',
              gradient,
              'backdrop-blur-[2px] border border-white/[0.15]',
              'shadow-[0_8px_32px_0_rgba(255,255,255,0.1)]',
              'after:absolute after:inset-0 after:rounded-full',
              'after:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2),transparent_70%)]'
            )}
          />
        </motion.div>
      </motion.div>
    )
  }
)

ElegantShape.displayName = 'ElegantShape'

function HeroGeometric() {
  const { i18n } = useLingui()
  return (
    <div
      className={cn(
        'relative min-h-[calc(100vh-70px)] w-full flex items-center justify-center overflow-hidden bg-background'
      )}
    >
      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-br',
          'from-[#04759C]/15 via-transparent to-[#1e90ff]/15 blur-3xl',
          'dark:from-[#04759C]/5 dark:via-transparent dark:to-[#1e90ff]/5'
        )}
      />

      <div className='absolute inset-0 overflow-hidden'>
        <ElegantShape
          delay={0.3}
          width={600}
          height={140}
          rotate={12}
          gradient='from-[#ff4500]/35 dark:from-[#4b0082]/15'
          className='left-[-10%] top-[15%]'
        />

        <ElegantShape
          delay={0.5}
          width={500}
          height={120}
          rotate={-15}
          gradient='from-[#b22222]/35 dark:from-[#b22222]/15'
          className='right-[-5%] top-[70%]'
        />

        <ElegantShape
          delay={0.4}
          width={300}
          height={80}
          rotate={-8}
          gradient='from-[#8a2be2]/35 dark:from-[#8a2be2]/15'
          className='left-[5%] bottom-[5%]'
        />

        <ElegantShape
          delay={0.6}
          width={200}
          height={60}
          rotate={20}
          gradient='from-[#ff8c00]/35 dark:from-[#ff8c00]/15'
          className='right-[15%] top-[10%]'
        />

        <ElegantShape
          delay={0.7}
          width={150}
          height={40}
          rotate={-25}
          gradient='from-[#00b7eb]/35 dark:from-[#00b7eb]/25'
          className='left-[20%] top-[5%]'
        />
      </div>

      <div className='relative z-10 container mx-auto px-4'>
        <div className='max-w-4xl mx-auto text-center'>
          <h1 className='text-4xl sm:text-6xl md:text-[80px] 2xl:text-8xl font-bold mb-6 tracking-tight leading-tight'>
            <span
              className={
                'bg-clip-text text-transparent bg-gradient-to-b from-[#c4c8c8] to-[#00b7eb] dark:from-white dark:to-white/80'
              }
            >
              {i18n._('Pione Group')}
            </span>
            <br />
            <span
              className={
                'bg-clip-text text-transparent bg-gradient-to-r from-[#00b7eb]/90 via-gray-300 to-[#b22222]/50 dark:from-[#00b7eb]/90 dark:via-white/90 dark:to-chart/90'
              }
            >
              {i18n._('Real - World AI Blockchain Solutions')}
            </span>
          </h1>

          <p
            className={
              'text-base md:text-lg text-muted-foreground mb-8 leading-relaxed tracking-wide max-w-xl mx-auto px-4'
            }
          >
            {i18n._(
              'Pione Group pioneers in building a comprehensive technology ecosystem, connecting data, operations, and digital value.'
            )}
          </p>
        </div>
      </div>

      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-t pointer-events-none',
          'from-white/80 via-transparent to-white/80',
          'dark:from-[#030303] dark:via-transparent dark:to-[#030303]/80'
        )}
      />
    </div>
  )
}

export { HeroGeometric }
